/* eslint-disable no-empty */
import * as Yup from 'yup'
import React from 'react'
import type { TFunction } from 'react-i18next'
import { Text as ChakraText } from '@chakra-ui/react'

import type UserSessionResponse from '../app/interfaces/UserSessionResponse'

export const initialUrlEndpointsFormState: {
  urlPrefix: string
  desc: string
  preferenceList: string[]
  urlEndpointFunctionTitle?: string 
  urlEndpointFunctionVersion?: number
} = {
  urlPrefix: '',
  desc: '',
  preferenceList: [],
}

export const UrlEndpointsSchema = Yup.object().shape({
  // generic field validations
  urlPrefix: Yup.string()
    .min(1, 'Minimum length should be 1')
    .required('Required')
    .matches(
      /^[a-zA-Z0-9-]+$/u,
      'Identifier can only contain alphabets, numbers, and hyphens',
    ),
  desc: Yup.string()
    .min(1, 'Minimum length should be 1')
    .required('Required')
    .matches(/^(?!\s*$).+/u, 'Description cannot be a blank string'),
  urlEndpointFunctionTitle: Yup.string().test(
    'is-version-selected',
    'Please select version',
    function (value) {
      const { urlEndpointFunctionVersion } = this.parent
      if (!value) {
        return true
      }
      return !!(value && urlEndpointFunctionVersion)
    },
  ),
  urlEndpointFunctionVersion: Yup.number().test(
    'is-title-selected',
    'Please select title',
    function (value) {
      if (!value) {
        return true
      }
      const { urlEndpointFunctionTitle } = this.parent
      return !!(value && urlEndpointFunctionTitle)
    },
  ),
})

export const UrlEndpointsWithoutURLPrefixSchema = Yup.object().shape({
  desc: Yup.string()
    .min(1, 'Minimum length should be 1')
    .required('Required')
    .matches(/^(?!\s*$).+/u, 'Description cannot be a blank string'),
  urlEndpointFunctionTitle: Yup.string().test(
    'is-version-selected',
    'Please select version',
    function (value) {
      const { urlEndpointFunctionVersion } = this.parent
      if (!value) {
        return true
      }
      return !!(value && urlEndpointFunctionVersion)
    },
  ),
  urlEndpointFunctionVersion: Yup.number().test(
    'is-title-selected',
    'Please select title',
    function (value) {
      if (!value) {
        return true
      }
      const { urlEndpointFunctionTitle } = this.parent
      return !!(value && urlEndpointFunctionTitle)
    },
  ),
})

export const defaultSourceJson = {
  baseUrl: 'doesnotmatter',
  typeId: '2',
  id: '1',
  name: 'ImageKit Media Library',
  isDefault: true,
  associatedPatterns: [],
  associatedUrlEndpoints: [],
}

export const getUrlEndpointsFormFields = (
  user: UserSessionResponse | null,
  t: TFunction,
): Array<{
  fieldName: 'urlPrefix' | 'desc'
  label: string
  description({ identifier }: { identifier?: string }): React.ReactElement | string
  required?: boolean
  placeholder: string
}> => [
  {
    fieldName: 'urlPrefix',
    label: 'Identifier',
    description: ({ identifier }: { identifier?: string }) => (
      <React.Fragment>
        <ChakraText as="span">
          {t(
            'can_only_contain_alphanum_hyphens',
            'Can only contain alphabets, numbers and hyphens. Image URL endpoint will become:',
          )}
        </ChakraText>
        <br />
        <ChakraText as="span" fontWeight="medium">
          {` ${
            user ? user.baseUrlForClientImageFetch : '<imagekitId>'
          }/${identifier}${identifier !== "" ? "/" : ""}`}
        </ChakraText>
      </React.Fragment>
    ),
    required: true,
    placeholder: 'Enter an identifier',
  },
  {
    fieldName: 'desc',
    label: 'Description',
    description: () =>
      'To help you identify this URL endpoint, you can give it a name or a detailed description',
    required: true,
    placeholder: 'Enter a description',
  },
]